// Playback for standalone demonstrations, independent of media-card-carousel.
if (!customElements.get('media-video-player')) {
  customElements.define('media-video-player', class extends HTMLElement {
    connectedCallback() {
      if (this._videoController) return;
      this._videoController = new AbortController();
      this._motion ||= window.matchMedia('(prefers-reduced-motion: reduce)');
      this._desktop ||= window.matchMedia('(min-width: 1000px)');
      const options = { signal: this._videoController.signal };
      this._initVideos(options);
      this._motion.addEventListener('change', () => this._videos.forEach((record) => this._syncPlayback(record)), options);
      this._desktop.addEventListener('change', () => this._videos.forEach((record) => this._selectSource(record)), options);
      document.addEventListener('visibilitychange', () => this._videos.forEach((record) => this._syncPlayback(record)), options);
    }

    _initVideos(options) {
      this._videos = [...this.querySelectorAll('[data-media-video], [data-carousel-video]')].map((video) => {
        const card = video.closest('[data-video-player], [data-carousel-slide]');
        const record = {
          video,
          cover: video.parentElement.querySelector('picture'),
          button: card.querySelector('[data-video-toggle]'),
          playIcon: card.querySelector('[data-video-play-icon]'),
          pauseIcon: card.querySelector('[data-video-pause-icon]'),
          progress: card.querySelector('[data-video-progress-current]'),
          error: card.querySelector('[data-video-error]'),
          visible: false,
          userPaused: false,
          manualPlay: false,
          blocked: false,
          pending: false,
          frameCallback: null,
          frameToken: 0,
          generation: 0,
          near: false,
          loaded: false,
          src: '',
        };
        // Keep HTML sources as a no-JS fallback; JS attaches only the active
        // viewport's source when this card approaches the visible area.
        video.querySelectorAll('source').forEach((source) => source.remove());
        // The responsive picture underneath is the cover. Avoid requesting a
        // second poster URL (or a desktop poster on mobile).
        this._setVideoReady(record, false);
        video.controls = false;
        video.muted = true;
        record.button.hidden = false;
        record.button.addEventListener('click', () => {
          if (!video.paused || record.pending) {
            record.userPaused = true;
            record.manualPlay = false;
            this._pause(record);
          } else {
            record.userPaused = false;
            record.manualPlay = true;
            record.blocked = false;
            if (video.error) video.load();
            this._play(record);
          }
        }, options);
        video.addEventListener('playing', () => {
          this._revealVideoAfterFrame(record);
          record.error.hidden = true;
          this._updateVideoControl(record, 'pause');
        }, options);
        video.addEventListener('loadeddata', () => this._revealVideoAfterFrame(record), options);
        video.addEventListener('loadedmetadata', () => this._updateVideoProgress(record), options);
        video.addEventListener('durationchange', () => this._updateVideoProgress(record), options);
        video.addEventListener('timeupdate', () => this._updateVideoProgress(record), options);
        video.addEventListener('pause', () => this._updateVideoControl(record, video.error ? 'retry' : 'play'), options);
        video.addEventListener('error', () => {
          record.blocked = true;
          this._setVideoReady(record, false);
          record.pending = false;
          record.error.hidden = false;
          this._updateVideoControl(record, 'retry');
        }, options);
        this._selectSource(record);
        return record;
      });

      if ('IntersectionObserver' in window) {
        this._loadObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const record = this._videos.find((item) => item.video === entry.target);
            if (!record) return;
            record.near = entry.isIntersecting;
            if (record.near) this._loadVideo(record);
          });
        }, { rootMargin: '240px' });
        this._videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const record = this._videos.find((item) => item.video === entry.target);
            if (!record) return;
            record.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
            if (!record.visible) record.manualPlay = false;
            this._syncPlayback(record);
          });
        }, { threshold: [0, 0.5] });
        this._videos.forEach(({ video }) => {
          this._loadObserver.observe(video);
          this._videoObserver.observe(video);
        });
      }
    }

    _setVideoReady(record, ready) {
      if (!ready) {
        record.frameToken += 1;
        if (record.frameCallback !== null) {
          record.video.cancelVideoFrameCallback?.(record.frameCallback);
          record.frameCallback = null;
        }
      }
      record.video.classList.toggle('opacity-0', !ready);
      // Poster and transcoded video can have slightly different aspect ratios.
      // Hide the cover without changing layout once a video frame is available.
      record.cover?.classList.toggle('invisible', ready);
    }

    _revealVideoAfterFrame(record) {
      if (record.frameCallback !== null) return;
      const { video } = record;
      const token = record.frameToken;
      const reveal = () => {
        if (token !== record.frameToken) return;
        record.frameCallback = null;
        if (!this.isConnected || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
        this._setVideoReady(record, true);
      };
      if (!video.paused && typeof video.requestVideoFrameCallback === 'function') {
        record.frameCallback = video.requestVideoFrameCallback(reveal);
      } else {
        // A paused video with loaded data may not submit another frame.
        requestAnimationFrame(reveal);
      }
    }

    _updateVideoControl(record, action) {
      const label = { play: 'Play video', pause: 'Pause video', retry: 'Retry video' }[action];
      record.button.setAttribute('aria-label', label);
      record.button.title = label;
      record.playIcon.hidden = action === 'pause';
      record.pauseIcon.hidden = action !== 'pause';
    }

    _updateVideoProgress(record) {
      const duration = Number.isFinite(record.video.duration) ? Math.max(0, record.video.duration) : 0;
      const current = Number.isFinite(record.video.currentTime) ? Math.max(0, record.video.currentTime) : 0;
      if (record.progress) {
        const ratio = duration > 0 ? Math.min(1, current / duration) : 0;
        record.progress.style.width = `${ratio * 100}%`;
      }
    }

    _selectSource(record) {
      const { video } = record;
      const src = this._desktop.matches ? video.dataset.desktopSrc : video.dataset.mobileSrc;
      if (src && src !== record.src) {
        this._pause(record);
        record.blocked = false;
        record.error.hidden = true;
        record.src = src;
        this._setVideoReady(record, false);
        if (record.loaded) {
          video.removeAttribute('src');
          video.preload = 'none';
          video.load();
          record.loaded = false;
        }
        if (record.near) this._loadVideo(record);
        this._syncPlayback(record);
      }
    }

    _loadVideo(record) {
      if (record.loaded || !record.src) return;
      record.loaded = true;
      const { video } = record;
      video.preload = 'metadata';
      video.src = record.src;
      video.load();
    }

    _syncPlayback(record) {
      const autoplay = this.hasAttribute('autoplay-video') && !this._motion.matches && !record.userPaused;
      if (!document.hidden && record.visible && !record.blocked && (autoplay || record.manualPlay)) this._play(record);
      else {
        this._pause(record);
        // Keep the decoded frame when leaving view or resizing. The cover is
        // only needed while loading a source or recovering from a media error.
      }
    }

    _play(record) {
      if (record.pending || !record.video.paused) return;
      this._loadVideo(record);
      record.pending = true;
      this._updateVideoControl(record, 'pause');
      const generation = ++record.generation;
      Promise.resolve(record.video.play()).then(() => {
        if (record.generation !== generation) return;
        if (!this.isConnected || document.hidden) record.video.pause();
      }).catch((error) => {
        if (record.generation !== generation || error.name === 'AbortError') return;
        // Autoplay can be denied; the same button remains a manual play control.
        record.blocked = true;
        this._updateVideoControl(record, record.video.error ? 'retry' : 'play');
      }).finally(() => {
        if (record.generation === generation) record.pending = false;
      });
    }

    _pause(record) {
      record.generation += 1;
      record.pending = false;
      record.video.pause();
      this._updateVideoControl(record, record.video.error ? 'retry' : 'play');
    }

    disconnectedCallback() {
      this._videoController?.abort();
      this._videoObserver?.disconnect();
      this._loadObserver?.disconnect();
      this._videos?.forEach((record) => {
        this._pause(record);
        this._setVideoReady(record, false);
        record.video.removeAttribute('src');
        record.video.load();
      });
      this._videoController = null;
    }
  });
}
